/**
 * CSV parse, header mapping, and row checks for vendor import.
 * PapaParse handles quoted commas, UTF-8 BOM, and CRLF.
 * Service area text becomes the same county and state values as the vendor form.
 */

import Papa from 'papaparse';
import {
  resolveVendorCategory,
} from './vendorCategories';
import {
  COUNTY_BY_FIPS,
  COUNTIES_BY_STATE,
  serviceAreaFromInput,
  shortCountyName,
  stateName,
} from './serviceArea';

const CSV_MAX_ROWS = 500;
const CSV_MAX_BYTES = 2 * 1024 * 1024;
const VENDOR_TEMPLATE_FILENAME = 'pacarion-vendor-upload-template.csv';

const VENDOR_IMPORT_HELP = 'Upload a CSV of providers you recommend. Each row is checked before it is added.';
const SERVICE_AREA_HINT = 'Use a county and state, like Orange County, FL, or a whole state, like Florida. Separate extra areas with a comma or semicolon, like Volusia County, FL, Seminole County, FL.';

const VENDOR_FIELDS = [
  { key: 'business_name', label: 'Business name', required: true },
  { key: 'category', label: 'Category', required: true },
  { key: 'contact_name', label: 'Contact name', required: false },
  { key: 'phone', label: 'Phone', required: true },
  { key: 'email', label: 'Email', required: false },
  { key: 'website', label: 'Website', required: false },
  { key: 'service_area', label: 'Service area', required: true },
  { key: 'notes', label: 'Notes', required: false },
  { key: 'discount', label: 'Discount', required: false },
  { key: 'discount_code', label: 'Discount code', required: false },
];

const HEADER_ALIASES = {
  'business name': 'business_name',
  business: 'business_name',
  'vendor name': 'business_name',
  vendor: 'business_name',
  company: 'business_name',
  name: 'business_name',
  category: 'category',
  categories: 'category',
  trade: 'category',
  trades: 'category',
  'contact name': 'contact_name',
  contact: 'contact_name',
  'contact person': 'contact_name',
  phone: 'phone',
  'phone number': 'phone',
  mobile: 'phone',
  cell: 'phone',
  email: 'email',
  'email address': 'email',
  website: 'website',
  url: 'website',
  web: 'website',
  'service area': 'service_area',
  'service areas': 'service_area',
  area: 'service_area',
  county: 'service_area',
  counties: 'service_area',
  serves: 'service_area',
  coverage: 'service_area',
  notes: 'notes',
  note: 'notes',
  description: 'notes',
  comments: 'notes',
  discount: 'discount',
  'discount offer': 'discount',
  'discount code': 'discount_code',
  code: 'discount_code',
  coupon: 'discount_code',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const STATE_BY_NAME = {};
Object.keys(COUNTIES_BY_STATE).forEach((code) => {
  STATE_BY_NAME[code.toLowerCase()] = code;
  STATE_BY_NAME[stateName(code).toLowerCase()] = code;
});

function areaKey(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/\./g, '')
    .replace(/[’']/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

const COUNTY_INDEX = new Map();
function addCountyKey(key, fips) {
  const normalized = areaKey(key);
  if (!normalized || COUNTY_INDEX.has(normalized)) return;
  COUNTY_INDEX.set(normalized, fips);
}
Object.keys(COUNTY_BY_FIPS).forEach((fips) => {
  const county = COUNTY_BY_FIPS[fips];
  const code = county.state;
  const fullState = stateName(code);
  const short = shortCountyName(county.name);
  [
    [county.name, code],
    [county.name, fullState],
    [short, code],
    [short, fullState],
    [`${short} County`, code],
    [`${short} County`, fullState],
    [`${short} Parish`, code],
    [`${short} Parish`, fullState],
  ].forEach(([left, right]) => {
    addCountyKey(`${left}, ${right}`, fips);
    addCountyKey(`${left} ${right}`, fips);
  });
});

function normalizeHeader(header) {
  return String(header || '')
    .replace(/^\uFEFF/, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function autoMatchVendorHeaders(headers) {
  const mapping = {};
  VENDOR_FIELDS.forEach((field) => {
    mapping[field.key] = null;
  });
  const used = new Set();
  (headers || []).forEach((header) => {
    const field = HEADER_ALIASES[normalizeHeader(header)];
    if (!field || used.has(field)) return;
    mapping[field] = header;
    used.add(field);
  });
  return mapping;
}

function vendorMappingMissing(mapping) {
  return VENDOR_FIELDS
    .filter((field) => field.required && !(mapping && mapping[field.key]))
    .map((field) => field.key);
}

function applyVendorMapping(row, mapping) {
  const out = {};
  VENDOR_FIELDS.forEach((field) => {
    const header = mapping && mapping[field.key];
    if (!header) return;
    out[field.key] = row[header];
  });
  return out;
}

function byteLength(text) {
  let bytes = 0;
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    if (code < 0x80) bytes += 1;
    else if (code < 0x800) bytes += 2;
    else if (code >= 0xd800 && code <= 0xdbff) {
      bytes += 4;
      i += 1;
    } else bytes += 3;
  }
  return bytes;
}

function parseVendorCsv(text) {
  const raw = String(text == null ? '' : text);
  if (byteLength(raw) > CSV_MAX_BYTES) {
    return { ok: false, code: 'file_too_large', message: 'CSV files must be 2 MB or smaller.', headers: [], rows: [] };
  }
  const cleaned = raw.replace(/^\uFEFF/, '');
  const parsed = Papa.parse(cleaned, { header: true, skipEmptyLines: 'greedy' });
  const headers = (parsed.meta.fields || []).filter((header) => String(header || '').trim() !== '');
  const fatal = (parsed.errors || []).find((err) => err.type === 'Quotes' || err.code === 'UndetectableDelimiter');
  if (fatal && (!parsed.data || parsed.data.length === 0)) {
    return { ok: false, code: 'parse_error', message: 'Could not read that CSV. Save the sheet as CSV and try again.', headers, rows: [] };
  }
  if (parsed.data.length > CSV_MAX_ROWS) {
    return {
      ok: false,
      code: 'too_many_rows',
      message: `CSV imports are limited to ${CSV_MAX_ROWS} vendors.`,
      headers,
      rows: [],
    };
  }
  const rows = parsed.data.map((row, index) => ({ rowNumber: index + 2, values: row }));
  return { ok: true, headers, rows, parseErrors: parsed.errors || [] };
}

function blank(value) {
  return String(value == null ? '' : value).trim();
}

function stateCode(text) {
  const key = areaKey(text);
  const code = STATE_BY_NAME[key];
  if (!code || !COUNTIES_BY_STATE[code]) return '';
  return code;
}

function parseAreaPart(part) {
  const text = blank(part).replace(/\s+/g, ' ');
  if (!text) return { error: true };
  const lead = text.match(/^(?:all of|entire|statewide|whole)\s+(.+)$/i);
  if (lead) {
    const code = stateCode(lead[1]);
    if (code) return { statewide: code };
    return { error: true };
  }
  const indexed = COUNTY_INDEX.get(areaKey(text));
  if (indexed) return { fips: indexed };
  if (!text.includes(',') && !/\b(county|parish|borough|census area|municipio)\b/i.test(text)) {
    const code = stateCode(text);
    if (code) return { statewide: code };
  }
  return { error: true };
}

function parseAreaList(text) {
  const direct = parseAreaPart(text);
  if (!direct.error) return { items: [direct] };
  const tokens = text.split(',').map((part) => part.trim()).filter(Boolean);
  if (tokens.length < 2) return { error: text };
  const items = [];
  let index = 0;
  while (index < tokens.length) {
    if (index + 1 < tokens.length) {
      const pair = parseAreaPart(`${tokens[index]}, ${tokens[index + 1]}`);
      if (!pair.error) {
        items.push(pair);
        index += 2;
        continue;
      }
    }
    const single = parseAreaPart(tokens[index]);
    if (!single.error) {
      items.push(single);
      index += 1;
      continue;
    }
    return { error: tokens.slice(index).join(', ') };
  }
  return items.length ? { items } : { error: text };
}

function parseServiceAreaText(value) {
  const raw = blank(value);
  if (!raw) return { countyFips: [], statewide: [], error: `Add a service area. ${SERVICE_AREA_HINT}` };
  const parts = raw.split(/[;|\n]+/).map((part) => part.trim()).filter(Boolean);
  const countyFips = [];
  const statewide = [];
  for (let i = 0; i < parts.length; i += 1) {
    const parsed = parseAreaList(parts[i]);
    if (parsed.error) {
      return {
        countyFips: [],
        statewide: [],
        error: `Couldn't match "${parsed.error}". ${SERVICE_AREA_HINT}`,
      };
    }
    parsed.items.forEach((item) => {
      if (item.fips && !countyFips.includes(item.fips)) countyFips.push(item.fips);
      if (item.statewide && !statewide.includes(item.statewide)) statewide.push(item.statewide);
    });
  }
  const checked = serviceAreaFromInput({
    service_county_fips: countyFips,
    service_statewide: statewide,
  });
  if (checked.error) return { countyFips: [], statewide: [], error: SERVICE_AREA_HINT };
  return { countyFips: checked.countyFips, statewide: checked.statewide, error: '' };
}

function parseCategories(value, extras) {
  const parts = blank(value).split(/[|,;]/).map((part) => part.trim()).filter(Boolean);
  if (parts.length === 0) return { error: 'Category is required' };
  const names = [];
  for (let i = 0; i < parts.length; i += 1) {
    const resolved = resolveVendorCategory(parts[i], extras);
    if (resolved.settlement) return { error: "Pacarion doesn't list settlement-service providers." };
    if (!resolved.name) return { error: 'Use a category from your list, such as HVAC or Plumbing.' };
    if (!names.includes(resolved.name)) names.push(resolved.name);
  }
  return { names };
}

function parseWebsite(value) {
  const text = blank(value);
  if (!text) return { value: '' };
  const withScheme = /^https?:\/\//i.test(text) ? text : `https://${text}`;
  let url;
  try {
    url = new URL(withScheme);
  } catch (error) {
    return { error: 'Enter a website, like https://example.com' };
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return { error: 'Enter a website, like https://example.com' };
  }
  if (!url.hostname || url.hostname.indexOf('.') === -1) {
    return { error: 'Enter a website, like https://example.com' };
  }
  return { value: withScheme };
}

function validateVendorValues(values, options = {}) {
  const source = values || {};
  const errors = {};
  const name = blank(source.business_name);
  if (!name) errors.business_name = 'Business name is required';
  else if (name.length > 200) errors.business_name = 'Must be 200 characters or fewer';

  const categories = parseCategories(source.category, options.categories);
  if (categories.error) errors.category = categories.error;

  const phoneText = blank(source.phone);
  const digits = phoneText.replace(/\D/g, '');
  if (!phoneText) errors.phone = 'Phone is required';
  else if (phoneText.length > 30 || digits.length < 7) errors.phone = 'Enter a phone number';

  const email = blank(source.email).toLowerCase();
  if (email && (email.length > 254 || !EMAIL_RE.test(email))) errors.email = 'Enter a valid email address';

  const website = parseWebsite(source.website);
  if (website.error) errors.website = website.error;

  const area = parseServiceAreaText(source.service_area);
  if (area.error) errors.service_area = area.error;

  const contact = blank(source.contact_name);
  if (contact.length > 120) errors.contact_name = 'Must be 120 characters or fewer';
  const notes = blank(source.notes);
  if (notes.length > 2000) errors.notes = 'Must be 2000 characters or fewer';
  const discount = blank(source.discount);
  if (discount.length > 200) errors.discount = 'Must be 200 characters or fewer';
  const code = blank(source.discount_code);
  if (code.length > 40) errors.discount_code = 'Must be 40 characters or fewer';

  const ok = Object.keys(errors).length === 0;
  const description = [contact ? `Contact: ${contact}` : '', notes].filter(Boolean).join('\n\n');
  return {
    ok,
    errors,
    data: ok ? {
      name,
      category: categories.names[0],
      categories: categories.names,
      contact_name: contact,
      phone: phoneText,
      email,
      website: website.value,
      description,
      discount,
      code,
      service_county_fips: area.countyFips,
      service_statewide: area.statewide,
    } : null,
  };
}

function nameKey(name) {
  return blank(name).toLowerCase().replace(/\s+/g, ' ');
}

function buildVendorPreview(mappedRows, existingVendors, categories) {
  const existing = new Set((existingVendors || []).map((vendor) => nameKey(vendor && vendor.name)).filter(Boolean));
  const seen = new Set();
  const rows = (mappedRows || []).map((row) => {
    const values = row.values || {};
    const validated = validateVendorValues(values, { categories });
    const base = {
      row: row.rowNumber,
      name: blank(values.business_name),
      category: blank(values.category),
      phone: blank(values.phone),
      message: '',
      data: null,
    };
    if (!validated.ok) {
      return { ...base, status: 'error', message: Object.values(validated.errors)[0] || 'This row needs a fix' };
    }
    const key = nameKey(validated.data.name);
    if (existing.has(key)) {
      return { ...base, status: 'duplicate', message: 'Already in your vendors', data: validated.data };
    }
    if (seen.has(key)) {
      return { ...base, status: 'duplicate', message: 'Already in this file', data: validated.data };
    }
    seen.add(key);
    return { ...base, status: 'ready', message: '', data: validated.data, category: validated.data.categories.join(', ') };
  });
  return {
    rows,
    totals: {
      ready: rows.filter((row) => row.status === 'ready').length,
      duplicate: rows.filter((row) => row.status === 'duplicate').length,
      error: rows.filter((row) => row.status === 'error').length,
    },
  };
}

function buildVendorTemplateCsv() {
  return Papa.unparse({
    fields: VENDOR_FIELDS.map((field) => field.key),
    data: [
      ['Cool Breeze HVAC', 'HVAC', 'Sam Rivera', '555-0142', 'hello@coolbreeze.example', 'https://coolbreeze.example', 'Orange County, FL', 'Annual tune-ups, filters included', '15% off first service', 'COOL15'],
      ['Harbor Plumbing', 'Plumbing', '', '555-0190', '', '', 'Florida', 'Drain and leak calls', '', ''],
      ['Clear View Windows', 'Window Cleaning', '', '555-0166', '', '', 'Volusia County, FL, Seminole County, FL', '', '', ''],
    ],
  });
}

function rowsToCsv(rows) {
  if (!rows || rows.length === 0) return '';
  const fields = Object.keys(rows[0]);
  return Papa.unparse({ fields, data: rows.map((row) => fields.map((field) => row[field] == null ? '' : row[field])) });
}

export {
  CSV_MAX_ROWS,
  CSV_MAX_BYTES,
  VENDOR_TEMPLATE_FILENAME,
  VENDOR_IMPORT_HELP,
  SERVICE_AREA_HINT,
  VENDOR_FIELDS,
  normalizeHeader,
  autoMatchVendorHeaders,
  vendorMappingMissing,
  applyVendorMapping,
  parseVendorCsv,
  parseServiceAreaText,
  validateVendorValues,
  buildVendorPreview,
  buildVendorTemplateCsv,
  rowsToCsv,
};
